// packages/shared/src/schemas/scan.schema.ts
//
// Validated by the API pipe and by both frontends, so a contract change breaks
// the build rather than production.

import { z } from 'zod';
import { IMAGE } from '../constants';

export const faceBoxSchema = z.object({
  // Normalised 0-1, as FaceMesh reports it. Pixel coordinates would depend on
  // the capture resolution, which differs between desktop and phone.
  x: z.number().min(0).max(1),
  y: z.number().min(0).max(1),
  width: z.number().min(0).max(1),
  height: z.number().min(0).max(1),
});

export const createSessionSchema = z.object({
  locale: z.string().max(10).optional(),
  declaredAge: z.number().int().min(10).max(100).optional(),
});

export const uploadUrlSchema = z.object({
  contentType: z.enum(IMAGE.ACCEPTED_MIME),
  capturedOn: z.enum(['desktop', 'mobile']).default('desktop'),
});

export const analyzeSchema = z.object({
  imageKey: z.string().min(1).max(300),
  declaredAge: z.number().int().min(10).max(100).optional(),
  faceBox: faceBoxSchema.optional(),
});

export const joinSessionSchema = z.object({
  token: z.string().min(10).max(200),
});

export const leadSchema = z
  .object({
    scanId: z.string().uuid().optional(),
    firstName: z.string().max(80).optional(),
    lastName: z.string().max(80).optional(),
    email: z.string().email().optional(),
    phone: z.string().min(6).max(24).optional(),
    message: z.string().max(1000).optional(),
    // Separate from biometric consent, and never bundled with it.
    marketingOptIn: z.boolean().default(false),
  })
  // One contact method is the whole point of the form.
  .refine((value) => Boolean(value.email || value.phone), {
    message: 'Provide an email or a phone number.',
    path: ['email'],
  });

export const scanStatusSchema = z.enum([
  'PENDING',
  'UPLOADED',
  'ANALYZING',
  'COMPLETE',
  'FAILED',
  'REJECTED_QUALITY',
]);

export const scanEventSchema = z.object({
  sessionId: z.string(),
  type: z.enum(['connected', 'uploaded', 'analyzing', 'progress', 'complete', 'failed', 'ping']),
  label: z.string().optional(),
  scanId: z.string().optional(),
  thumbnailUrl: z.string().url().optional(),
  data: z.record(z.unknown()).optional(),
  error: z.object({ code: z.string(), message: z.string() }).optional(),
});

export type FaceBox = z.infer<typeof faceBoxSchema>;
export type CreateSessionInput = z.infer<typeof createSessionSchema>;
export type AnalyzeInput = z.infer<typeof analyzeSchema>;
export type LeadInput = z.infer<typeof leadSchema>;
export type ScanStatus = z.infer<typeof scanStatusSchema>;
export type ScanEvent = z.infer<typeof scanEventSchema>;
